import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import API_URL from '../api';

export default function Reports() {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReport, setShowReport] = useState(false);
  const [building, setBuilding] = useState(false);
  const [detailedJobs, setDetailedJobs] = useState([]);
  const [settings, setSettings] = useState({ company_name: 'JobCostNow', logo_url: '', phone: '', address: '', brand_color: '#2E4057' });

  useEffect(() => {
    axios.get(`${API_URL}/api/jobs`, { headers }).then(res => { setJobs(res.data); setLoading(false); }).catch(() => setLoading(false));
    axios.get(`${API_URL}/api/settings`).then(res => {
      if (res.data && res.data.company_name) {
        setSettings({
          company_name: res.data.company_name || 'JobCostNow',
          logo_url: res.data.logo_url || '',
          phone: res.data.phone || '',
          address: res.data.address || '',
          brand_color: res.data.brand_color || '#2E4057'
        });
      }
    }).catch(() => {});
  }, []);

  const fmt = (n) => '$' + parseFloat(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2 });
  const payTypeLabel = (t) => t === 'piece' ? 'Piece Rate' : t === 'commission' ? 'Commission' : 'Hourly';
  const brand = settings.brand_color || '#2E4057';

  // Build the full report: fetch each job's detail (COs, payments, materials, labor)
  const buildReport = async () => {
    setBuilding(true);
    try {
      const results = await Promise.all(
        jobs.map(j => axios.get(`${API_URL}/api/jobs/${j.id}`, { headers }).then(r => r.data).catch(() => null))
      );
      setDetailedJobs(results.filter(Boolean));
      setShowReport(true);
    } catch (e) {
      alert('Could not build report: ' + (e.response?.data?.error || e.message));
    }
    setBuilding(false);
  };

  // Per-job calculations from detailed data
  const calc = (job) => {
    const totalCOs = job.change_orders?.reduce((s, co) => s + parseFloat(co.amount), 0) || 0;
    const totalPaid = job.payments?.reduce((s, p) => s + parseFloat(p.amount), 0) || 0;
    const totalMaterials = job.materials?.reduce((s, m) => s + parseFloat(m.total_cost), 0) || 0;
    const totalLabor = job.labor?.reduce((s, l) => s + parseFloat(l.total), 0) || 0;
    const revised = parseFloat(job.contract_amount || 0) + totalCOs;
    const costs = totalMaterials + totalLabor;
    const profit = revised - costs;
    const margin = revised > 0 ? (profit / revised * 100) : 0;
    const balance = revised - totalPaid;
    return { totalCOs, totalPaid, totalMaterials, totalLabor, revised, costs, profit, margin, balance };
  };

  // Company grand totals
  const grand = detailedJobs.reduce((a, j) => {
    const c = calc(j);
    return {
      revised: a.revised + c.revised,
      paid: a.paid + c.totalPaid,
      materials: a.materials + c.totalMaterials,
      labor: a.labor + c.totalLabor,
      costs: a.costs + c.costs,
      profit: a.profit + c.profit,
      balance: a.balance + c.balance,
    };
  }, { revised: 0, paid: 0, materials: 0, labor: 0, costs: 0, profit: 0, balance: 0 });
  const grandMargin = grand.revised > 0 ? (grand.profit / grand.revised * 100).toFixed(1) : 0;
  const activeCount = detailedJobs.filter(j => j.status === 'active').length;

  if (loading) return <Layout><div style={{textAlign:'center',padding:'60px'}}>Loading...</div></Layout>;

  // ---- The printable report view ----
  if (showReport) {
    return (
      <div style={styles.wrap}>
        <div style={styles.toolbar} className="no-print">
          <button onClick={() => setShowReport(false)} style={styles.back}>&larr; Back to Reports</button>
          <button onClick={() => window.print()} style={{...styles.printBtn, background: brand}}>Print / Save PDF</button>
        </div>

        <div style={styles.page} id="report">
          <div style={{...styles.stripe, background: brand}}></div>

          {/* Header */}
          <div style={styles.repHeader}>
            <div style={styles.headLeft}>
              {settings.logo_url
                ? <img src={settings.logo_url} alt={settings.company_name} style={styles.logo} onError={e => { e.target.style.display='none'; }} />
                : <div style={{...styles.coName, color: brand}}>{settings.company_name}</div>}
              <div style={styles.coContact}>
                {settings.address ? <div>{settings.address}</div> : null}
                {settings.phone ? <div>{settings.phone}</div> : null}
              </div>
            </div>
            <div style={styles.headRight}>
              <div style={{...styles.repTitle, color: brand}}>COMPANY SUMMARY REPORT</div>
              <div style={styles.repMeta}>{new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</div>
              <div style={styles.confidential}>CONFIDENTIAL &middot; INTERNAL</div>
            </div>
          </div>

          {/* Headline metrics */}
          <div style={styles.metricRow}>
            <div style={styles.metric}><div style={styles.metricLbl}>TOTAL CONTRACT</div><div style={{...styles.metricVal, color: brand}}>{fmt(grand.revised)}</div></div>
            <div style={styles.metric}><div style={styles.metricLbl}>GROSS PROFIT</div><div style={{...styles.metricVal, color: '#1E7145'}}>{fmt(grand.profit)}</div></div>
            <div style={styles.metric}><div style={styles.metricLbl}>GROSS MARGIN</div><div style={{...styles.metricVal, color: '#1E7145'}}>{grandMargin}%</div></div>
            <div style={styles.metricLast}><div style={styles.metricLbl}>OUTSTANDING</div><div style={{...styles.metricVal, color: '#C0392B'}}>{fmt(grand.balance)}</div></div>
          </div>

          {/* Company financial summary */}
          <div style={{...styles.secBar, borderLeft: `4px solid ${brand}`}}>COMPANY FINANCIAL SUMMARY</div>
          <table style={styles.sumTable}>
            <tbody>
              <tr><td style={styles.sLabel}>Total Revised Contract Value</td><td style={styles.sVal}>{fmt(grand.revised)}</td></tr>
              <tr><td style={styles.sLabel}>Total Materials Cost</td><td style={styles.sVal}>{fmt(grand.materials)}</td></tr>
              <tr><td style={styles.sLabel}>Total Labor Cost</td><td style={styles.sVal}>{fmt(grand.labor)}</td></tr>
              <tr style={styles.hl}><td style={styles.sLabelB}>Total Costs</td><td style={styles.sValB}>{fmt(grand.costs)}</td></tr>
              <tr style={styles.profit}><td style={styles.sLabelB}>Total Gross Profit</td><td style={styles.sValB}>{fmt(grand.profit)}</td></tr>
              <tr style={styles.profit}><td style={styles.sLabelG}>Company Gross Margin</td><td style={styles.sValG}>{grandMargin}%</td></tr>
              <tr><td style={styles.sLabel}>Total Collected</td><td style={styles.sVal}>{fmt(grand.paid)}</td></tr>
              <tr style={styles.due}><td style={styles.sLabelR}>Total Outstanding Balance</td><td style={styles.sValR}>{fmt(grand.balance)}</td></tr>
            </tbody>
          </table>

          {/* Per-job detail sections */}
          <div style={{...styles.secBar, borderLeft: `4px solid ${brand}`}}>JOB DETAIL ({detailedJobs.length} jobs &middot; {activeCount} active)</div>

          {detailedJobs.map((job, idx) => {
            const c = calc(job);
            return (
              <div key={job.id} style={styles.jobBlock}>
                <div style={{...styles.jobHead, borderLeft: `4px solid ${brand}`}}>
                  <span style={styles.jobName}>{job.customer_name}</span>
                  <span style={styles.jobSub}>{job.job_number ? `Job #${job.job_number}` : ''}{job.job_address ? `  \u00b7  ${job.job_address}` : ''}</span>
                </div>

                {/* Job financial mini-summary */}
                <table style={styles.miniTable}>
                  <tbody>
                    <tr>
                      <td style={styles.miniLbl}>Revised Contract</td><td style={styles.miniVal}>{fmt(c.revised)}</td>
                      <td style={styles.miniLbl}>Total Costs</td><td style={styles.miniVal}>{fmt(c.costs)}</td>
                    </tr>
                    <tr>
                      <td style={styles.miniLbl}>Gross Profit</td><td style={{...styles.miniVal, color:'#1E7145'}}>{fmt(c.profit)} ({c.margin.toFixed(1)}%)</td>
                      <td style={styles.miniLbl}>Balance Due</td><td style={{...styles.miniVal, color: c.balance > 0 ? '#C0392B' : '#1E7145'}}>{fmt(c.balance)}</td>
                    </tr>
                  </tbody>
                </table>

                {/* Change Orders */}
                <div style={styles.subBar}>Change Orders</div>
                {(!job.change_orders || job.change_orders.length === 0) ? <p style={styles.empty}>None recorded</p> : (
                  <table style={styles.dtable}>
                    <thead><tr style={styles.headRowBg}><th style={styles.th}>Date</th><th style={styles.th}>Description</th><th style={styles.thR}>Amount</th><th style={styles.thC}>Status</th></tr></thead>
                    <tbody>{job.change_orders.map((co, i) => (
                      <tr key={i} style={i%2 ? styles.zebra : null}>
                        <td style={styles.td}>{co.co_date ? new Date(co.co_date).toLocaleDateString() : '\u2014'}</td>
                        <td style={styles.td}>{co.description}</td>
                        <td style={styles.tdR}>{fmt(co.amount)}</td>
                        <td style={styles.tdC}><span style={{color: co.signed ? '#1E7145' : '#E67E22', fontWeight: 600, fontSize: '11px'}}>{co.signed ? 'SIGNED' : 'VERBAL'}</span></td>
                      </tr>
                    ))}</tbody>
                    <tfoot><tr><td colSpan={2} style={styles.ftL}>Total</td><td style={styles.ftR}>{fmt(c.totalCOs)}</td><td style={styles.ftBlank}></td></tr></tfoot>
                  </table>
                )}

                {/* Payments */}
                <div style={styles.subBar}>Payments</div>
                {(!job.payments || job.payments.length === 0) ? <p style={styles.empty}>None recorded</p> : (
                  <table style={styles.dtable}>
                    <thead><tr style={styles.headRowBg}><th style={styles.th}>Date</th><th style={styles.th}>Description</th><th style={styles.th}>Method</th><th style={styles.thR}>Amount</th></tr></thead>
                    <tbody>{job.payments.map((p, i) => (
                      <tr key={i} style={i%2 ? styles.zebra : null}>
                        <td style={styles.td}>{p.payment_date ? new Date(p.payment_date).toLocaleDateString() : '\u2014'}</td>
                        <td style={styles.td}>{p.description || '\u2014'}</td>
                        <td style={styles.td}>{p.method || '\u2014'}</td>
                        <td style={styles.tdR}>{fmt(p.amount)}</td>
                      </tr>
                    ))}</tbody>
                    <tfoot><tr><td colSpan={3} style={styles.ftL}>Total Paid</td><td style={styles.ftR}>{fmt(c.totalPaid)}</td></tr></tfoot>
                  </table>
                )}

                {/* Materials */}
                <div style={styles.subBar}>Materials</div>
                {(!job.materials || job.materials.length === 0) ? <p style={styles.empty}>None recorded</p> : (
                  <table style={styles.dtable}>
                    <thead><tr style={styles.headRowBg}><th style={styles.th}>Date</th><th style={styles.th}>Description</th><th style={styles.th}>Vendor</th><th style={styles.thR}>Qty</th><th style={styles.thR}>Unit</th><th style={styles.thR}>Total</th></tr></thead>
                    <tbody>{job.materials.map((m, i) => (
                      <tr key={i} style={i%2 ? styles.zebra : null}>
                        <td style={styles.td}>{m.material_date ? new Date(m.material_date).toLocaleDateString() : '\u2014'}</td>
                        <td style={styles.td}>{m.description}</td>
                        <td style={styles.td}>{m.vendor || '\u2014'}</td>
                        <td style={styles.tdR}>{m.quantity}</td>
                        <td style={styles.tdR}>{fmt(m.unit_cost)}</td>
                        <td style={styles.tdR}>{fmt(m.total_cost)}</td>
                      </tr>
                    ))}</tbody>
                    <tfoot><tr><td colSpan={5} style={styles.ftL}>Total Materials</td><td style={styles.ftR}>{fmt(c.totalMaterials)}</td></tr></tfoot>
                  </table>
                )}

                {/* Labor */}
                <div style={styles.subBar}>Labor</div>
                {(!job.labor || job.labor.length === 0) ? <p style={styles.empty}>None recorded</p> : (
                  <table style={styles.dtable}>
                    <thead><tr style={styles.headRowBg}><th style={styles.th}>Date</th><th style={styles.th}>Employee</th><th style={styles.th}>Type</th><th style={styles.th}>Description</th><th style={styles.thR}>Hours</th><th style={styles.thR}>Rate</th><th style={styles.thR}>Total</th></tr></thead>
                    <tbody>{job.labor.map((l, i) => (
                      <tr key={i} style={i%2 ? styles.zebra : null}>
                        <td style={styles.td}>{l.labor_date ? new Date(l.labor_date).toLocaleDateString() : '\u2014'}</td>
                        <td style={styles.td}>{l.employee}</td>
                        <td style={styles.td}>{payTypeLabel(l.pay_type)}</td>
                        <td style={styles.td}>{l.description || '\u2014'}</td>
                        <td style={styles.tdR}>{l.pay_type === 'hourly' ? l.hours : '\u2014'}</td>
                        <td style={styles.tdR}>{l.pay_type === 'hourly' ? fmt(l.rate) : '\u2014'}</td>
                        <td style={styles.tdR}>{fmt(l.total)}</td>
                      </tr>
                    ))}</tbody>
                    <tfoot><tr><td colSpan={6} style={styles.ftL}>Total Labor</td><td style={styles.ftR}>{fmt(c.totalLabor)}</td></tr></tfoot>
                  </table>
                )}
              </div>
            );
          })}

          <div style={styles.repFoot}>
            <span>{settings.company_name}{settings.phone ? '  \u00b7  ' + settings.phone : ''}</span>
            <span>Confidential &mdash; Internal Use Only</span>
          </div>
        </div>

        <style>{`
          @media print {
            .no-print { display: none !important; }
            body { background: #fff !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            #report { box-shadow: none !important; margin: 0 !important; max-width: 100% !important; border-radius: 0 !important; }
            table { page-break-inside: auto; }
            tr { page-break-inside: avoid; }
            .jobBlock { page-break-inside: avoid; }
            @page { margin: 0.5in; }
          }
        `}</style>
      </div>
    );
  }

  // ---- The Reports landing page (inside the sidebar) ----
  return (
    <Layout>
      <div style={styles.topbar}>
        <h1 style={styles.pageTitle}>Reports</h1>
      </div>
      <div style={styles.body}>
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Company Summary Report</h2>
          <p style={styles.cardText}>
            A complete, branded snapshot of the whole company — total contract value, costs, profit,
            margin, outstanding balances, plus full change order, payment, material, and labor detail for every job. Print it or save as PDF.
          </p>
          <button onClick={buildReport} disabled={building} style={{...styles.btnPrimary, background: brand, opacity: building ? 0.6 : 1}}>
            {building ? 'Building report…' : 'View Company Report'}
          </button>
        </div>
        <div style={styles.cardMuted}>
          <p style={styles.mutedText}>More report types coming soon — by customer, by date range, and more.</p>
        </div>
      </div>
    </Layout>
  );
}

const styles = {
  topbar: { background: '#fff', padding: '20px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e6e9ed' },
  pageTitle: { color: '#2E4057', margin: 0, fontSize: '22px' },
  body: { padding: '32px', maxWidth: '700px', margin: '0 auto', width: '100%', boxSizing: 'border-box' },
  card: { background: '#fff', borderRadius: '10px', padding: '32px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: '16px' },
  cardTitle: { color: '#2E4057', fontSize: '18px', margin: '0 0 10px' },
  cardText: { color: '#666', fontSize: '14px', lineHeight: '1.6', margin: '0 0 20px' },
  btnPrimary: { padding: '12px 24px', background: '#2E4057', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '15px' },
  cardMuted: { background: '#f9fafb', borderRadius: '10px', padding: '20px', border: '1px dashed #dde2e8' },
  mutedText: { color: '#9aa5b1', fontSize: '13px', margin: 0, textAlign: 'center' },

  wrap: { minHeight: '100vh', background: '#eef1f5', fontFamily: "'Helvetica Neue', Arial, sans-serif", padding: '24px 0' },
  toolbar: { maxWidth: '820px', margin: '0 auto 18px', display: 'flex', justifyContent: 'space-between', padding: '0 16px' },
  back: { background: '#fff', color: '#2E4057', border: '1px solid #ccc', borderRadius: '6px', padding: '10px 18px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' },
  printBtn: { background: '#2E4057', color: '#fff', border: 'none', borderRadius: '6px', padding: '10px 22px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' },
  page: { maxWidth: '820px', margin: '0 auto', background: '#fff', boxShadow: '0 4px 20px rgba(0,0,0,0.10)', borderRadius: '4px', overflow: 'hidden' },
  stripe: { height: '6px', width: '100%' },
  repHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '32px 40px 20px' },
  headLeft: {},
  logo: { maxHeight: '64px', maxWidth: '240px', objectFit: 'contain', marginBottom: '8px' },
  coName: { fontSize: '26px', fontWeight: 'bold', letterSpacing: '-0.5px' },
  coContact: { fontSize: '11.5px', color: '#888', lineHeight: 1.5 },
  headRight: { textAlign: 'right' },
  repTitle: { fontSize: '17px', fontWeight: 'bold', letterSpacing: '1.5px' },
  repMeta: { fontSize: '12px', color: '#777', marginTop: '4px' },
  confidential: { fontSize: '9px', color: '#bbb', letterSpacing: '1px', marginTop: '6px' },
  metricRow: { display: 'flex', padding: '0 40px', margin: '22px 0 8px' },
  metric: { flex: 1, borderRight: '1px solid #eceff2', paddingRight: '16px' },
  metricLast: { flex: 1, paddingLeft: '16px' },
  metricLbl: { fontSize: '9px', color: '#9aa5b1', letterSpacing: '0.8px', marginBottom: '4px' },
  metricVal: { fontSize: '20px', fontWeight: 'bold', letterSpacing: '-0.5px' },
  secBar: { fontSize: '11px', fontWeight: 'bold', color: '#2E4057', letterSpacing: '1px', padding: '7px 0 7px 12px', margin: '26px 40px 10px', background: '#f6f8fa' },
  sumTable: { width: 'calc(100% - 80px)', margin: '0 40px', borderCollapse: 'collapse', fontSize: '13.5px' },
  sLabel: { padding: '7px 8px', color: '#555', borderBottom: '1px solid #f2f4f6' },
  sVal: { padding: '7px 8px', textAlign: 'right', color: '#333', borderBottom: '1px solid #f2f4f6', fontVariantNumeric: 'tabular-nums' },
  sLabelB: { padding: '8px', fontWeight: 'bold', color: '#2E4057' },
  sValB: { padding: '8px', textAlign: 'right', fontWeight: 'bold', color: '#2E4057', fontVariantNumeric: 'tabular-nums' },
  sLabelG: { padding: '7px 8px', color: '#1E7145', fontWeight: 600 },
  sValG: { padding: '7px 8px', textAlign: 'right', color: '#1E7145', fontWeight: 600, fontVariantNumeric: 'tabular-nums' },
  sLabelR: { padding: '8px', fontWeight: 'bold', color: '#C0392B' },
  sValR: { padding: '8px', textAlign: 'right', fontWeight: 'bold', color: '#C0392B', fontVariantNumeric: 'tabular-nums' },
  hl: { background: '#f0f4f8' },
  profit: { background: '#EAF6EE' },
  due: { background: '#FBEAE8' },
  jobBlock: { margin: '0 40px 28px', paddingBottom: '8px', borderBottom: '1px solid #eceff2' },
  jobHead: { display: 'flex', flexDirection: 'column', background: '#f6f8fa', padding: '8px 12px', margin: '20px 0 12px' },
  jobName: { fontSize: '15px', fontWeight: 'bold', color: '#2E4057' },
  jobSub: { fontSize: '11px', color: '#888', marginTop: '2px' },
  miniTable: { width: '100%', borderCollapse: 'collapse', fontSize: '12.5px', marginBottom: '8px' },
  miniLbl: { padding: '5px 8px', color: '#9aa5b1', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px', width: '22%' },
  miniVal: { padding: '5px 8px', color: '#2E4057', fontWeight: 600, fontVariantNumeric: 'tabular-nums', width: '28%' },
  subBar: { fontSize: '10px', fontWeight: 'bold', color: '#888', letterSpacing: '0.8px', textTransform: 'uppercase', margin: '14px 0 6px' },
  dtable: { width: '100%', borderCollapse: 'collapse', fontSize: '12px', marginBottom: '4px' },
  headRowBg: { background: '#2E4057' },
  th: { padding: '7px 8px', textAlign: 'left', fontSize: '9px', color: '#fff', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 },
  thR: { padding: '7px 8px', textAlign: 'right', fontSize: '9px', color: '#fff', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 },
  thC: { padding: '7px 8px', textAlign: 'center', fontSize: '9px', color: '#fff', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 },
  zebra: { background: '#f8fafb' },
  td: { padding: '6px 8px', color: '#333', borderBottom: '1px solid #f2f4f6' },
  tdR: { padding: '6px 8px', textAlign: 'right', color: '#333', borderBottom: '1px solid #f2f4f6', fontVariantNumeric: 'tabular-nums' },
  tdC: { padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #f2f4f6' },
  ftL: { padding: '8px', textAlign: 'right', fontWeight: 'bold', color: '#2E4057', borderTop: '2px solid #2E4057', fontSize: '11px' },
  ftR: { padding: '8px', textAlign: 'right', fontWeight: 'bold', color: '#2E4057', borderTop: '2px solid #2E4057', fontVariantNumeric: 'tabular-nums', fontSize: '11px' },
  ftBlank: { borderTop: '2px solid #2E4057' },
  empty: { color: '#aaa', fontSize: '12px', fontStyle: 'italic', margin: '2px 0 8px' },
  repFoot: { marginTop: '36px', padding: '14px 40px', borderTop: '1px solid #eceff2', fontSize: '10px', color: '#aaa', display: 'flex', justifyContent: 'space-between' },
};